/**
 * Auth Provider
 * Manages authentication state with AWS Cognito
 */

import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signIn, signOut, signUp, fetchAuthSession } from 'aws-amplify/auth';
import cognitoConfig from './cognito';
import { UserRole } from '../types';
import mockUsers from '../mocks/users.json';

// Configure Amplify
Amplify.configure({
  Auth: {
    Cognito: cognitoConfig,
  },
});

interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  displayName: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string, role: UserRole) => Promise<void>;
  isAuthenticated: boolean;
  hasRole: (role: UserRole) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMockSession, setIsMockSession] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      
      // Extract user info from session
      const groups = session.tokens?.accessToken?.payload['cognito:groups'] as string[] || [];
      const role = groups[0] as UserRole || 'PATIENT';
      
      setUser({
        id: currentUser.userId,
        email: currentUser.signInDetails?.loginId || '',
        role,
        displayName: currentUser.username,
      });
      setIsMockSession(false); // This is a real Cognito session
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      
      // Check if this is a mock user (dev mode)
      const allMockUsers = [...mockUsers.patients, ...mockUsers.nutritionists, ...mockUsers.admins];
      const mockUser = allMockUsers.find(u => u.email === email && u.password === password);
      
      if (mockUser) {
        // Mock authentication for demo accounts
        setUser({
          id: mockUser.id,
          email: mockUser.email,
          role: mockUser.role as UserRole,
          displayName: `${mockUser.firstName} ${mockUser.lastName}`,
        });
        setIsMockSession(true);
        setLoading(false);
        return;
      }
      
      // Real Cognito authentication for non-mock users
      await signIn({ username: email, password });
      setIsMockSession(false);
      await checkAuth();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      // Only call Cognito signOut if this is a real session (not a mock user)
      if (!isMockSession) {
        await signOut();
      }
      setUser(null);
      setIsMockSession(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Logout failed';
      setError(message);
      throw err;
    }
  };

  const register = async (email: string, password: string, _role: UserRole) => {
    try {
      setError(null);
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });
      // Note: Additional step needed to add user to Cognito group for role
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(message);
      throw err;
    }
  };

  const hasRole = (role: UserRole): boolean => {
    return user?.role === role;
  };

  const value: AuthContextValue = {
    user,
    loading,
    error,
    login,
    logout,
    register,
    isAuthenticated: !!user,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
